/**
 * Feature flags — read from window.__ENV__, which is injected at runtime by
 * the Express /config.js endpoint (see server.js). Flags can therefore be
 * toggled via server environment variables without a frontend rebuild.
 *
 * Precedence is resolved server-side and differs per key — see
 * utils/featureFlagResolver.js:
 *   - balance, commissions: an explicitly-set SHOW_* env var wins;
 *     GotaSettings.features (stamped by the business preset) is the default.
 *   - facturacion, esignature: GotaSettings.features wins; the env var is the
 *     fallback for keys the preset does not define.
 *   - warranties, contractTemplates: GotaSettings.features only, no env var.
 *   - termsOfService, privacyPolicy, homePage: env var only, no DB key.
 *
 * Most flags default to false (hidden) when neither source enables them; the
 * two rental-vertical modules below deliberately default to true.
 */
const flags = (typeof window !== 'undefined' && window.__ENV__) || {};

export const isBalanceEnabled = () => flags.SHOW_BALANCE === true;
export const isCommissionsEnabled = () => flags.SHOW_COMMISSIONS === true;
export const isFacturacionEnabled = () => flags.SHOW_FACTURACION === true;
export const isTermsOfServiceEnabled = () => flags.SHOW_TERMS_OF_SERVICE === true;
export const isPrivacyPolicyEnabled = () => flags.SHOW_PRIVACY_POLICY === true;
export const isHomePageEnabled = () => flags.SHOW_HOME_PAGE === true;
// Rental-vertical modules: default ON (undefined = enabled) so existing
// deployments keep them until a business preset explicitly turns them off.
export const isWarrantiesEnabled = () => flags.SHOW_WARRANTIES !== false;
export const isContractTemplatesEnabled = () => flags.SHOW_CONTRACT_TEMPLATES !== false;
export const isEsignatureEnabled = () => flags.SHOW_ESIGNATURE === true;
// Gotasoft ERP work (see docs/gotasoft-erp.md) — all default OFF; the backend
// gates the same features with utils/featureFlags.js requireFeature().
export const isBillPayersEnabled = () => flags.SHOW_BILL_PAYERS === true;
export const isBillEstimatesEnabled = () => flags.SHOW_BILL_ESTIMATES === true;
export const isBillSourcesEnabled = () => flags.SHOW_BILL_SOURCES === true;
export const isGoogleConnectionsEnabled = () => flags.SHOW_GOOGLE_CONNECTIONS === true;
export const isBankReconciliationEnabled = () => flags.SHOW_BANK_RECONCILIATION === true;
// AI agents (MCP) acting as the signed-in user — docs/mcp-plan.md
export const isMcpEnabled = () => flags.SHOW_MCP === true;
// Documents (issued invoices) attached to each debt due
export const isDueDocumentsEnabled = () => flags.SHOW_DUE_DOCUMENTS === true;
// One collection (bank transfer) covering several dues
export const isPaymentGroupsEnabled = () => flags.SHOW_PAYMENT_GROUPS === true;
// One business behind several legal entities (account "continúa a")
export const isAccountChainEnabled = () => flags.SHOW_ACCOUNT_CHAIN === true;
// Attribution by own entity and center (centros de atribución) and its report
export const isAttributionEnabled = () => flags.SHOW_ATTRIBUTION === true;
// Top menu grouped in dropdowns by ObjectTab.group (ERP presets) and the group
// editor in Configuración › Pestañas; flat menu when off.
export const isNavGroupsEnabled = () => flags.SHOW_NAV_GROUPS === true;
// IRPF / IVA tracking toggles in Configuración › Contabilidad (utils/taxSettings.js)
export const isTaxSettingsEnabled = () => flags.SHOW_TAX_SETTINGS === true;
// "Estado de cuenta" PDF for selected dues of a debt or credit
export const isClientStatementEnabled = () => flags.SHOW_CLIENT_STATEMENT === true;
// Business preset (inmobiliaria | software | servicios), from GotaSettings.
// Real-estate wording such as the balance's "Pago a inmobiliaria" only shows
// for inmobiliaria, the default when the server sends nothing.
export const getBusinessPreset = () => flags.BUSINESS_PRESET || 'inmobiliaria';
export const isRealEstatePreset = () => getBusinessPreset() === 'inmobiliaria';

/**
 * Company logo for on-screen chrome. Both variants ship per client (see
 * frontend/scripts/select-client-assets.js); the light one is the default
 * because it suited the original colored navbar. The topbar is now white
 * (components/Topbar/topbar.css), so clients whose logo is white serve the dark
 * variant instead — decided server-side from CLIENT, see DARK_LOGO_CLIENTS in
 * server.js.
 */
export const getCompanyLogoSrc = () =>
	(flags.USE_DARK_LOGO === true ? '/companyLogoNegro.png' : '/companyLogo.png');
